/** Space between the anchor and the panel. */
export const ANCHOR_GAP = 6;
/** Extra space when the panel opens to the right (sidebar flyouts). */
export const SIDE_FLYOUT_EXTRA_GAP = 4;
/** Minimum distance kept between the panel and the viewport edges. */
export const VIEWPORT_MARGIN = 8;

export type PopoverAlign = "start" | "end";
export type PopoverSide = "bottom" | "right";

export interface PopoverPosition {
  top: number;
  left: number;
  /** Where the panel ended up relative to the anchor. */
  side: "top" | "bottom";
  /** Set when the panel fits on neither side: it is cut to this height and scrolls. */
  maxHeight?: number;
}

interface Size {
  width: number;
  height: number;
}

interface AnchorBox {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface PlacementGeometry {
  anchor: AnchorBox;
  panel: Size;
  viewport: Size;
}

export interface PlacementPreference {
  align: PopoverAlign;
  side: PopoverSide;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(min, value), max);
}

function placeToTheRight({ anchor, panel, viewport }: PlacementGeometry): PopoverPosition {
  const top = clamp(anchor.top, VIEWPORT_MARGIN, viewport.height - panel.height - VIEWPORT_MARGIN);
  const left = Math.min(anchor.right + ANCHOR_GAP + SIDE_FLYOUT_EXTRA_GAP, viewport.width - panel.width - VIEWPORT_MARGIN);
  return { top, left, side: "bottom" };
}

/**
 * Below the anchor, flipped above only when it does not fit below but does fit above. When it fits on
 * neither side, it opens on the side with more room and is cut to that room (it scrolls).
 */
function placeBelowOrAbove({ anchor, panel, viewport }: PlacementGeometry, align: PopoverAlign): PopoverPosition {
  const below = anchor.bottom + ANCHOR_GAP;
  const roomBelow = viewport.height - VIEWPORT_MARGIN - below;
  const roomAbove = anchor.top - ANCHOR_GAP - VIEWPORT_MARGIN;
  const alignedLeft = align === "end" ? anchor.right - panel.width : anchor.left;
  const left = clamp(alignedLeft, VIEWPORT_MARGIN, viewport.width - panel.width - VIEWPORT_MARGIN);
  if (panel.height <= roomBelow) return { top: below, left, side: "bottom" };
  if (panel.height < roomAbove) return { top: anchor.top - ANCHOR_GAP - panel.height, left, side: "top" };
  if (roomBelow >= roomAbove) return { top: below, left, side: "bottom", maxHeight: roomBelow };
  return { top: VIEWPORT_MARGIN, left, side: "top", maxHeight: roomAbove };
}

/** Viewport coordinates for the panel, kept inside the viewport. */
export function placePopover(geometry: PlacementGeometry, preference: PlacementPreference): PopoverPosition {
  if (preference.side === "right") return placeToTheRight(geometry);
  return placeBelowOrAbove(geometry, preference.align);
}

/** CSS transform origin so the opening animation grows out of the anchor. */
export function transformOrigin(side: PopoverPosition["side"] | undefined, align: PopoverAlign): string {
  return `${side === "top" ? "bottom" : "top"} ${align === "end" ? "right" : "left"}`;
}
